/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { getVouchers } from '~src/services/api/contracts';
import { getOfferVouchers } from '~src/services/api/offers';
import { OffersEvaluationView } from './OffersEvaluationView';
import {
  EvaluationContractLike,
  EvaluationOfferLike,
  EvaluationVoucherResponseLike,
} from './evaluationData';

type OffersEvaluationContractViewProps = {
  projectId: number;
  tenderId: number;
  contract: EvaluationContractLike;
  offers: EvaluationOfferLike[];
  isEvaluationLocked?: boolean;
};

export const OffersEvaluationContractView: FC<OffersEvaluationContractViewProps> = ({
  projectId,
  tenderId,
  contract,
  offers,
  isEvaluationLocked,
}) => {
  const intl = useIntl();

  const [voucherResponsesByOfferId, setVoucherResponsesByOfferId] = useState<
    Record<number, EvaluationVoucherResponseLike[]>
  >({});

  const [isResponsesLoading, setIsResponsesLoading] = useState(false);

  const contractOffers = useMemo(
    () => offers.filter((offer) => offer.contract_id === contract.id),
    [offers, contract.id]
  );

  const contractOfferIds = useMemo(
    () => contractOffers.map((offer) => offer.id),
    [contractOffers]
  );

  // Stable signal so the responses effect only re-runs when the SET of offers
  // changes — not when an offer object reference changes (e.g. after a review
  // dashboard cache patch). Prevents a full responses refetch + spinner flash.
  const contractOfferIdsKey = contractOfferIds.join(',');

  const {
    data: vouchers = [],
    isLoading: isVouchersLoading,
    isError: isVouchersError,
  } = useQuery<any[]>(
    ['evaluation-contract-vouchers', projectId, tenderId, contract.id],
    () =>
      getVouchers({
        projectId: Number(projectId),
        tenderId: Number(tenderId),
        contractId: Number(contract.id),
      }),
    {
      enabled: !!projectId && !!tenderId && !!contract.id,
    }
  );

  useEffect(() => {
    let isMounted = true;

    const loadResponses = async () => {
      if (!projectId || !tenderId || !contractOfferIds.length) {
        setVoucherResponsesByOfferId({});
        return;
      }

      setIsResponsesLoading(true);

      try {
        const entries = await Promise.all(
          contractOfferIds.map(async (offerId) => {
            try {
              const responses = await getOfferVouchers({
                projectId,
                tenderId,
                offerId,
              });

              return [offerId, responses || []] as [
                number,
                EvaluationVoucherResponseLike[]
              ];
            } catch (error) {
              return [offerId, []] as [number, EvaluationVoucherResponseLike[]];
            }
          })
        );

        if (!isMounted) return;

        setVoucherResponsesByOfferId(
          entries.reduce<Record<number, EvaluationVoucherResponseLike[]>>(
            (acc, [offerId, responses]) => {
              acc[offerId] = responses;
              return acc;
            },
            {}
          )
        );
      } finally {
        if (isMounted) {
          setIsResponsesLoading(false);
        }
      }
    };

    loadResponses();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, tenderId, contractOfferIdsKey]);

  if (isVouchersLoading || isResponsesLoading) {
    return <Spin />;
  }

  if (isVouchersError) {
    return (
      <Alert
        type="warning"
        showIcon
        message={intl.formatMessage({
          id: 'evaluation-contract-vouchers-load-error-message',
        })}
        description={intl.formatMessage({
          id: 'evaluation-contract-vouchers-load-error-description',
        })}
      />
    );
  }

  return (
    <OffersEvaluationView
      projectId={projectId}
      tenderId={tenderId}
      contract={contract}
      offers={offers}
      vouchers={vouchers}
      voucherResponsesByOfferId={voucherResponsesByOfferId}
      isEvaluationLocked={isEvaluationLocked ?? Number(contract.state) !== 6}
    />
  );
};
